import React from 'react';
import { MapPin, DollarSign, Calendar, Users, Plane, Car, Ship, Train, Clock, Heart, ChevronRight } from 'lucide-react';

export default function TripCard({ trip, onClick, currentUser, family }) {
  // Extract trip metadata
  const metadata = trip.trip_metadata || {};
  const images = metadata.images || {};
  
  // Get transport icon
  const getTransportIcon = () => {
    const iconClass = "w-4 h-4";
    switch (metadata.transport_type) {
      case 'fly': return <Plane className={iconClass} />;
      case 'drive': return <Car className={iconClass} />;
      case 'cruise': return <Ship className={iconClass} />;
      case 'train': return <Train className={iconClass} />;
      default: return <Car className={iconClass} />;
    }
  };
  
  // Get duration display
  const getDurationDisplay = () => {
    const durations = {
      'overnight': 'Overnight',
      'weekend': '2-3 days',
      '4-7days': '4-7 days',
      '1-2weeks': '1-2 weeks',
      '2+weeks': '2+ weeks'
    };
    return durations[metadata.duration_category] || 'TBD';
  };
  
  // Get trip phase badge color
  const getPhaseColor = () => {
    switch (metadata.trip_phase) {
      case 'idea': return 'bg-azure-100 text-azure-800';
      case 'researching': return 'bg-amber-100 text-amber-800';
      case 'planning': return 'bg-violet-100 text-violet-800';
      case 'booked': return 'bg-emerald-100 text-emerald-800';
      case 'completed': return 'bg-stone-100 text-stone-800';
      default: return 'bg-stone-100 text-stone-800';
    }
  };
  
  // Get phase display name
  const getPhaseDisplay = () => {
    const phases = {
      'idea': '💡 Idea',
      'researching': '🔍 Researching',
      'planning': '📋 Planning',
      'booked': '✅ Booked',
      'completed': '🎉 Completed'
    };
    return phases[metadata.trip_phase] || '💡 Idea';
  };
  
  // Calculate budget range
  const getBudgetDisplay = () => {
    if (metadata.budget_breakdown?.totals) {
      const totals = metadata.budget_breakdown.totals;
      // Check if we have the new format with per_person and per_person_max
      if (totals.per_person !== undefined) {
        if (totals.per_person_max && totals.per_person_max !== totals.per_person) {
          return `$${totals.per_person}-${totals.per_person_max}/person`;
        }
        return `$${totals.per_person}/person`;
      }
    }
    return 'Budget TBD';
  };
  
  // Get interested members
  const interestedMembers = metadata.interested_members || [];
  const familyMembers = [...(family?.parents || []), ...(family?.kids || [])];
  const interestedCount = interestedMembers.length;
  const totalMembers = familyMembers.length;
  
  // Get family rating
  const getFamilyRating = () => {
    const ratings = Object.values(trip.ratings || {});
    if (ratings.length === 0) return null;
    
    const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
    return average.toFixed(1);
  };
  
  const familyRating = getFamilyRating();
  
  // Get trip features display
  const getTripFeatures = () => {
    const features = metadata.trip_features || [];
    const displayFeatures = features.slice(0, 3).map(f => {
      const featureMap = {
        'domestic': '🇺🇸',
        'international': '🌍',
        'coastal': '🏖️',
        'mountains': '⛰️',
        'desert': '🏜️',
        'city': '🏙️',
        'theme_parks': '🎢',
        'national_parks': '🏞️',
        'resort': '🏨',
        'adventure': '🎯',
        'relaxation': '🧘',
        'cultural': '🎭',
        'food_focused': '🍽️'
      };
      return featureMap[f] || '';
    });
    return displayFeatures.filter(Boolean);
  };
  
  return (
    <div 
      onClick={(e) => onClick(trip, e.currentTarget)}
      className="bg-white rounded-xl border border-stone-200 hover:border-stone-300 hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden flex flex-col"
      style={{ minHeight: '400px' }}
    >
      {/* Image Collage Section */}
      <div className="relative h-48 bg-stone-100">
        {images.main || images.secondary || images.tertiary || trip.image ? (
          <div className="flex h-full">
            {/* Main Image - 60% width */}
            <div className="w-3/5 h-full">
              <img 
                src={images.main || trip.image || '/api/placeholder/400/300'}
                alt={trip.title}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Secondary Images - 40% width */}
            {(images.secondary || images.tertiary) && (
              <div className="w-2/5 h-full flex flex-col">
                <div className="h-1/2 border-l border-b border-white">
                  <img 
                    src={images.secondary || '/api/placeholder/200/150'}
                    alt={`${trip.title} 2`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="h-1/2 border-l border-white">
                  <img 
                    src={images.tertiary || '/api/placeholder/200/150'}
                    alt={`${trip.title} 3`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-azure-100 to-teal-100">
            <div className="text-6xl">✈️</div>
          </div>
        )}
        
        {/* Phase Badge */}
        <div className={`absolute top-3 left-3 px-3 py-1.5 rounded-full text-xs font-semibold ${getPhaseColor()}`}>
          {getPhaseDisplay()}
        </div>
        
        {/* Trip Features */}
        {getTripFeatures().length > 0 && (
          <div className="absolute top-3 right-3 flex gap-1">
            {getTripFeatures().map((emoji, idx) => (
              <span key={idx} className="text-lg bg-white/90 rounded-full p-1">
                {emoji}
              </span>
            ))}
          </div>
        )}
      </div>
      
      {/* Content Section */}
      <div className="flex-1 p-4 flex flex-col">
        {/* Trip Type & Duration */}
        <div className="flex items-center gap-3 text-sm text-stone-600 mb-2">
          <div className="flex items-center gap-1">
            {getTransportIcon()}
            <span className="capitalize">{metadata.transport_type || 'Drive'}</span>
          </div>
          <span>•</span>
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{getDurationDisplay()}</span>
          </div>
          {metadata.travel_time && (
            <>
              <span>•</span>
              <span className="text-xs">{metadata.travel_time}</span>
            </>
          )}
        </div>
        
        {/* Title & Location */}
        <h3 className="text-lg font-bold text-stone-900 mb-1 line-clamp-2">
          {trip.title}
        </h3>
        {trip.location && (
          <div className="flex items-center gap-1 text-stone-600 text-sm mb-3">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{trip.location}</span>
          </div>
        )}
        
        {/* Budget Highlight */}
        <div className="bg-gold-50 border border-gold-200 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-gold-600" />
              <span className="text-sm font-semibold text-gold-900">
                {getBudgetDisplay()}
              </span>
            </div>
            {metadata.preferred_season && (
              <span className="text-xs text-gold-700">
                Best in {metadata.preferred_season}
              </span>
            )}
          </div>
        </div>
        
        {/* Interest Tracker */}
        {metadata.trip_phase === 'idea' && (
          <div className="bg-azure-50 border border-azure-200 rounded-lg p-3 mb-3">
            <div className="text-xs font-semibold text-azure-900 mb-2">
              Who's interested? ({interestedCount}/{totalMembers})
            </div>
            <div className="flex flex-wrap gap-1">
              {familyMembers.slice(0, 5).map(member => {
                const isInterested = interestedMembers.includes(member.id);
                return (
                  <div
                    key={member.id}
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      isInterested 
                        ? 'bg-azure-500 text-white' 
                        : 'bg-azure-100 text-azure-600'
                    }`}
                  >
                    {member.name.split(' ')[0]}
                  </div>
                );
              })}
              {familyMembers.length > 5 && (
                <div className="px-2 py-1 rounded-full text-xs font-medium bg-azure-100 text-azure-600">
                  +{familyMembers.length - 5}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Rating or Action */}
        <div className="mt-auto flex items-center justify-between">
          {familyRating ? (
            <div className="flex items-center gap-1">
              <span className="text-yellow-500">⭐</span>
              <span className="font-semibold text-stone-900">{familyRating}</span>
              <span className="text-sm text-stone-500">/ 10</span>
            </div>
          ) : (
            <div className="text-sm text-stone-500">No ratings yet</div>
          )}
          
          <ChevronRight className="w-5 h-5 text-stone-400" />
        </div>
      </div>
    </div>
  );
}